import { type Check, type Smoke, api } from "../lib/api.ts";
import { accountStatus, spendPosition } from "../lib/format.ts";
import { overviewCopy } from "../lib/overview-copy.ts";
import { type Load, useLoad } from "../lib/useLoad.ts";
/**
 * The screen an operator lands on after SSO.
 *
 * Its job is to answer one question: what can I act on right now, and is the
 * machinery behind it up? The spend rail carries the weight — with no
 * platform-side risk gate in this system, the Meta account spending limit is
 * the only hard stop, so an account without one is stated plainly rather than
 * left to be discovered.
 */
import { Badge, Card, InfoBanner, Page } from "../ui/index.ts";

export function Overview() {
	const [access] = useLoad(() => api.myAccess());
	const [accounts] = useLoad(() => api.fbAccounts());
	const [smoke] = useLoad(() => api.smoke());

	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	/*
	 * One call, and this screen only renders — sentences and banners come back
	 * finished. It holds no cap counts and no banner conditions, because both
	 * were places the unreadable state could be folded into "no spending limit"
	 * with every guard still green. Composition lives in lib/overview-copy.ts,
	 * where a runner can reach it.
	 */
	const copy = overviewCopy(
		accounts.state,
		list,
		accounts.state === "ready" && accounts.data.truncated,
	);

	return (
		<Page
			title={copy.headline}
			description={copy.subhead}
			actions={
				access.state === "ready" ? (
					<Badge variant={access.data.inherited ? "info" : "default"}>
						{roleLabel(access.data.role)}
					</Badge>
				) : null
			}
		>
			{copy.banners.map((b) => (
				<InfoBanner key={b.title} variant={b.kind} title={b.title}>
					{b.body}
				</InfoBanner>
			))}

			{list.length > 0 && (
				<ul className="mt-6 grid list-none grid-cols-[repeat(auto-fill,minmax(272px,1fr))] gap-4 p-0">
					{list.map((a) => {
						const status = accountStatus(a.accountStatus ?? -1);
						const spend = spendPosition(a.spendCap, a.amountSpent, a.currency);
						return (
							<li key={a.id}>
								<Card className="flex h-full flex-col gap-3 p-5">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<h3 className="font-semibold text-content">{a.name}</h3>
											<p className="mt-0.5 font-mono text-xs text-muted">{a.metaAccountId}</p>
										</div>
										<Badge variant={badgeVariant(status.tone)}>{status.label}</Badge>
									</div>
									{/*
									 * The rail is the limit drawn literally: a line to a stop. The fill
									 * is how much of it is gone — the whole point of the change, since a
									 * cap nobody has spent against and a cap at 99% are the same sentence
									 * without it.
									 *
									 * No fill is drawn when the spend was not read, rather than a bar at
									 * zero. An empty rail reads as "nothing spent", and that is a claim
									 * about somebody's account made on a failure to read our own mirror.
									 *
									 * Every word and number here comes from `spendPosition`. The screen
									 * chooses nothing, for the reason `overviewCopy` exists: a `.tsx` is
									 * unreachable by the runner, so a decision made here is a decision
									 * nothing can check.
									 *
									 * That claim is held by three cases in `format.test.ts`, and they are
									 * permissive rather than prohibitive: what this file may import, which
									 * fields it may read, and that every read is followed by the end of its
									 * expression. Passing the decision to a helper or branching on it is not
									 * rendering it, and fails by default rather than when somebody thinks to
									 * forbid the shape.
									 */}
									<div className={spend.rail}>
										<div className="rail-track">
											{spend.fill && (
												<div className={spend.fill.className} style={{ width: spend.fill.width }} />
											)}
										</div>
										<div className="rail-label">
											<span>{spend.label}</span>
											<strong>{spend.text}</strong>
										</div>
									</div>
								</Card>
							</li>
						);
					})}
				</ul>
			)}

			{accounts.state === "failed" && (
				<InfoBanner variant="danger" title="Ad accounts could not be read.">
					{accounts.why}
				</InfoBanner>
			)}

			<section className="mt-10 border-t border-line pt-6">
				<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">Systems</p>
				<div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-px bg-line ring-1 ring-line">
					<System name="Meta Marketing API" load={smoke} pick={(d) => d.meta} />
					<System name="BigQuery" load={smoke} pick={(d) => d.bq} />
					<System name="Database" load={smoke} pick={(d) => d.db} />
				</div>
			</section>
		</Page>
	);
}

const DOT: Record<string, string> = {
	ok: "bg-success",
	error: "bg-danger",
	skipped: "bg-muted",
	unknown: "bg-muted",
};

/**
 * A dependency line names what it actually reached — which database on which
 * instance, which dataset. "Connected" alone does not answer "connected to
 * what?", and on a shared instance that question has a real wrong answer.
 */
function System({
	name,
	load,
	pick,
}: {
	name: string;
	load: Load<Smoke>;
	pick: (d: Smoke) => Check | undefined;
}) {
	const check = load.state === "ready" ? pick(load.data) : undefined;
	const state = load.state === "failed" ? "error" : (check?.state ?? "unknown");
	const target = check?.target;
	const detail =
		load.state === "failed"
			? "Status check unavailable"
			: load.state === "loading"
				? "Checking…"
				: (check?.detail ?? "");

	return (
		<div className="flex items-start gap-3 bg-surface p-4">
			<span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${DOT[state]}`} aria-hidden="true" />
			<div className="min-w-0">
				<p className="text-sm font-semibold text-content">{name}</p>
				{target && <p className="mt-0.5 break-words font-mono text-xs text-content">{target}</p>}
				<p className="mt-0.5 break-words text-xs text-muted">{detail}</p>
			</div>
		</div>
	);
}

export function badgeVariant(tone: string): "success" | "warning" | "error" | "default" {
	if (tone === "ok") return "success";
	if (tone === "warn") return "warning";
	if (tone === "err") return "error";
	return "default";
}

function roleLabel(role: string): string {
	return role === "super_admin"
		? "Super admin"
		: role === "admin"
			? "Admin"
			: role === "manager"
				? "Manager"
				: "User";
}
